// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { ChangeEvent, Component, createContext, Dispatch, ReactNode, SetStateAction, useState } from 'react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import Chart, { ChartDataSets } from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'moment/locale/nl-be';
import { Box, Button, Flex, Input, Spinner, Text } from '@chakra-ui/core';
import { format, subMonths } from 'date-fns';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Bounds, DatasetsSupplier } from './types';

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
export const PartitionKeysContext = createContext<{
  partitionKeys: string[];
  setPartitionKeys: Dispatch<string[] | undefined>;
}>(null!);

interface ConnectChartProps {
  startDate: Date;
  endDate?: Date;
  useFetchDataSets: (bounds: Bounds, partitionKeys?: string) => readonly [ChartDataSets[], boolean];
  quarterly?: boolean;
  comparison?: ReactNode;
}

const ConnectChart: React.FC<ConnectChartProps> = ({ startDate, endDate, useFetchDataSets, quarterly, comparison }) => {
  const [{ min, max }, setBounds] = useState<Bounds>({
    max: endDate,
  });
  const [partitionKeys, setPartitionKeys] = useState<string[] | undefined>();
  const [animationLoading, setAnimationLoading] = useState(false);
  const [datasets, datasetsLoading] = useFetchDataSets(
    {
      min,
      max,
    },
    partitionKeys?.join(',')
  );

  return (
    <PartitionKeysContext.Provider value={{ partitionKeys: partitionKeys ?? [], setPartitionKeys }}>
      <Box position="relative">
        {!quarterly && (
          <Line
            data={{ datasets }}
            options={{
              animation: {
                onComplete: () => setAnimationLoading(false),
              },
              elements: {
                point: { radius: 0 },
              },
              tooltips: {
                mode: 'index',
                intersect: false,
              },
              scales: {
                xAxes: [
                  {
                    type: 'time',
                    time: {
                      minUnit: 'day',
                      tooltipFormat: "D MMMM 'YY",
                      displayFormats: {
                        day: 'D MMM',
                      },
                    },
                  },
                ],
                yAxes: [
                  {
                    ticks: {
                      suggestedMin: 95,
                      suggestedMax: 105,
                    },
                  },
                ],
              },
            }}
          />
        )}
        {quarterly && (
          <Bar
            data={{ datasets }}
            options={{
              animation: {
                onComplete: () => setAnimationLoading(false),
              },
              tooltips: {
                mode: datasets.length >= 5 ? 'x' : 'index',
                intersect: false,
              },
              scales: {
                xAxes: [
                  {
                    offset: true,
                    type: 'time',
                    ticks: {
                      source: 'data',
                    },
                    time: {
                      unit: 'quarter',
                      tooltipFormat: '[Q]Q YYYY',
                    },
                  },
                ],
                yAxes: [
                  {
                    ticks: {
                      suggestedMin: -10,
                      suggestedMax: 10,
                    },
                  },
                ],
              },
            }}
          />
        )}
        {(datasetsLoading || animationLoading) && (
          <Flex
            top={0}
            position="absolute"
            justify="center"
            align="center"
            w="100%"
            h="100%"
            bg="rgba(255,255,255,0.5)"
          >
            <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="#b28e5c" size="xl" />
          </Flex>
        )}
      </Box>
      <Flex my={4} justify="space-between">
        <Flex gridGap={2}>
          <Button rounded={0} onClick={() => setBounds({})}>
            vanaf aanvang
          </Button>
          <Button
            onClick={() =>
              setBounds({
                min: subMonths(endDate ? new Date(endDate) : new Date(), 12),
              })
            }
          >
            1j
          </Button>
          <Button
            rounded={0}
            onClick={() =>
              setBounds({
                min: subMonths(endDate ? new Date(endDate) : new Date(), 6),
              })
            }
          >
            6m
          </Button>
          <Button
            rounded={0}
            onClick={() =>
              setBounds({
                min: subMonths(endDate ? new Date(endDate) : new Date(), 3),
              })
            }
          >
            3m
          </Button>
          <Button
            rounded={0}
            onClick={() =>
              setBounds({
                min: subMonths(endDate ? new Date(endDate) : new Date(), 1),
              })
            }
          >
            1m
          </Button>
        </Flex>
        <Flex gridGap={2} align="center" justify="center">
          <Input
            rounded={0}
            type="date"
            w="auto"
            min={format(startDate, 'yyyy-MM-dd')}
            value={format(min ?? startDate, 'yyyy-MM-dd')}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              if (e.target.value)
                setBounds({
                  min: new Date(e.target.value),
                  max,
                });
            }}
            isRequired
          />
          <Text>Tot</Text>
          <Input
            rounded={0}
            type="date"
            w="auto"
            max={endDate ? format(endDate, 'yyyy-MM-dd') : undefined}
            value={format(max ?? endDate ?? new Date(), 'yyyy-MM-dd')}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              if (e.target.value)
                setBounds({
                  min,
                  max: new Date(e.target.value),
                });
            }}
            isRequired
          />
        </Flex>
      </Flex>
      {comparison}
    </PartitionKeysContext.Provider>
  );
};
export default ConnectChart;
